/**
 * Client Detail Page (Admin)
 * View and edit client details
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAdminApi } from '../../../api/admin/clients.api';
import Badge from '../../../components/ui/Badge';

interface Client {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  countryCode?: string;
  phoneNumber?: string;
  status: string;
  creditBalance: number;
  taxExempt: number;
  marketingEmails: number;
  createdAt: string;
  lastLoginAt?: string;
  notes?: string;
}

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'services' | 'invoices' | 'contacts'>('overview');

  useEffect(() => {
    loadClient();
  }, [id]);

  const loadClient = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const response = await clientsAdminApi.get(parseInt(id));
      setClient(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Error Loading Client</h2>
          <p className="text-slate-400 mb-6">{error || 'Client not found'}</p>
          <button
            onClick={() => navigate('/admin/clients')}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg"
          >
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
      active: 'success',
      inactive: 'default',
      suspended: 'warning',
      closed: 'danger',
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-slate-800/80 border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/admin/clients')}
              className="text-slate-400 hover:text-white"
            >
              ← Back to Clients
            </button>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(`/admin/clients/${id}/edit`)}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg"
              >
                Edit Client
              </button>
            </div>
          </div>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                {client.firstName} {client.lastName}
              </h1>
              {client.companyName && (
                <p className="text-slate-400 mt-1">{client.companyName}</p>
              )}
            </div>
            <div>{getStatusBadge(client.status)}</div>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex space-x-6 border-b border-slate-700/50">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'services', label: 'Services' },
              { key: 'invoices', label: 'Invoices' },
              { key: 'contacts', label: 'Contacts' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`pb-3 border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-orange-500 text-white'
                    : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Information */}
            <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm text-slate-400">Email</dt>
                  <dd className="text-white">{client.email}</dd>
                </div>
                {client.phoneNumber && (
                  <div>
                    <dt className="text-sm text-slate-400">Phone</dt>
                    <dd className="text-white">{client.phoneNumber}</dd>
                  </div>
                )}
                {client.address1 && (
                  <div>
                    <dt className="text-sm text-slate-400">Address</dt>
                    <dd className="text-white">
                      {client.address1}
                      {client.address2 && <>, {client.address2}</>}
                      <br />
                      {client.city && `${client.city}, `}
                      {client.state} {client.postcode}
                      <br />
                      {client.countryCode}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Account Details */}
            <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
              <h3 className="text-lg font-semibold mb-4">Account Details</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm text-slate-400">Credit Balance</dt>
                  <dd className={`text-lg font-semibold ${client.creditBalance > 0 ? 'text-green-400' : 'text-white'}`}>
                    ${client.creditBalance.toFixed(2)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-400">Tax Exempt</dt>
                  <dd className="text-white">{client.taxExempt ? 'Yes' : 'No'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-400">Marketing Emails</dt>
                  <dd className="text-white">{client.marketingEmails ? 'Enabled' : 'Disabled'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-slate-400">Created</dt>
                  <dd className="text-white">{new Date(client.createdAt).toLocaleString()}</dd>
                </div>
                {client.lastLoginAt && (
                  <div>
                    <dt className="text-sm text-slate-400">Last Login</dt>
                    <dd className="text-white">{new Date(client.lastLoginAt).toLocaleString()}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Notes */}
            {client.notes && (
              <div className="md:col-span-2 bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
                <h3 className="text-lg font-semibold mb-4">Notes</h3>
                <p className="text-slate-300 whitespace-pre-wrap">{client.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'services' && (
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <h3 className="text-lg font-semibold mb-4">Services</h3>
            <p className="text-slate-400">No services found for this client.</p>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <h3 className="text-lg font-semibold mb-4">Invoices</h3>
            <p className="text-slate-400">No invoices found for this client.</p>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <h3 className="text-lg font-semibold mb-4">Contacts</h3>
            <p className="text-slate-400">No contacts found for this client.</p>
          </div>
        )}
      </div>
    </div>
  );
}
